/**
 * Que dice cada cosa que se ve.
 *
 * Antes **todo el color iba a la prioridad** y la barra no tenia con que decir nada mas: ni si
 * la fecha la puso alguien, ni cuanto avanzo. Tres cosas que importan compitiendo por un solo
 * canal, y ganaba la que menos decide.
 *
 * Ahora cada canal dice una sola cosa:
 *
 * - **relleno** = progreso
 * - **contorno** = estado de la fecha (solida = la puso una persona, punteada = la dedujo el
 *   sistema)
 * - **marca chica al costado del nombre** = prioridad
 *
 * La franja de carga usa una rampa de un solo tono, y pasarse de la capacidad no se marca solo
 * con color sino con **textura** y cruzando la linea: un rojo/verde solo deja afuera a quien no
 * los distingue, y ademas obliga a recordar cual es cual.
 */

export const COLOR = {
  marca: '#002FA7',
  pista: '#E0E6F6',
  ticket: '#3F3F46',
  fase: '#A1A1AA',
  finde: '#FAFAFA',
  grilla: '#F4F4F5',
  inferido: '#71717A',
  peligro: '#FF2A00',
  aviso: '#FFB800',
};

/** La rampa de carga: un solo tono, cuatro pasos. El quinto no es color, es textura. */
export const RAMPA_DE_CARGA = ['#DCE3F6', '#A9BBE7', '#5878C6', '#002FA7'];

export function colorDeCarga(pct) {
  if (pct > 100) return RAMPA_DE_CARGA[3];
  if (pct > 85) return RAMPA_DE_CARGA[2];
  if (pct > 50) return RAMPA_DE_CARGA[1];
  return RAMPA_DE_CARGA[0];
}

const PRIORIDAD = {
  critical: '#DC2626',
  high: '#EA580C',
  medium: '#CA8A04',
  low: '#65A30D',
};

/** El color que la capsula declara para cada prioridad. Sin prioridad, sin marca. */
export function colorDePrioridad(prioridad) {
  return PRIORIDAD[prioridad] || 'transparent';
}

/** Compatibilidad: la guia dibuja sus muestras con las mismas clases que el grafico. */
export function priorityColor(priority) {
  const map = {
    critical: 'border-l-4 border-l-red-500',
    high: 'border-l-4 border-l-orange-500',
    medium: 'border-l-4 border-l-blue-400',
    low: 'border-l-4 border-l-zinc-300',
  };
  return map[priority] || '';
}

export function barClass(row) {
  if (row.row_type === 'ticket') return 'bg-zinc-700';
  if (row.row_type === 'phase') return 'bg-zinc-500';
  return 'bg-brand-primary';
}

export function barStyle(row) {
  const inferido = row.start_inferred || row.end_inferred;
  return inferido ? 'border border-dashed border-zinc-400 opacity-80' : '';
}

/** Si esta fecha la decidio alguien o la dedujo el sistema. */
export function fechaDeducida(row) {
  return Boolean(row?.start_inferred || row?.end_inferred);
}

/**
 * La marca de "no entra" en la fila del ticket.
 *
 * Solo se marca lo que **no** entra: si tambien se marcara lo que entra, el ojo tendria que
 * leer cuatro estados en una columna que ya tiene el nombre, la prioridad y el responsable, y
 * el que importa dejaria de saltar. Lo demas lo cuenta la tarjeta al pasar por encima.
 */
export function marcaDeCompromiso(row) {
  const c = row?.compromiso;
  if (!c || row.row_type !== 'ticket') return null;
  if (c.estado === 'no_entra') {
    return { texto: `+${Math.abs(c.margen_dias)} d`, clase: 'text-brand-danger border-red-200 bg-red-50' };
  }
  if (c.estado === 'sin_compromiso' && c.compromiso_roto) {
    return { texto: 'sin calcular', clase: 'text-amber-700 border-amber-200 bg-amber-50' };
  }
  return null;
}
